/**
 * AboutPage — static-content page at `/about`.
 *
 * Web port of Flutter's `AboutScreen`
 * (`lib/presentation/screens/about/about_screen.dart:8-62`). Renders a
 * back-button + centered "About Expause" app bar over a scrolling column of
 * four static section cards (exclusive content → LIVE streaming → top
 * monetization → privacy), each an `AboutSectionCard`. There is no server
 * fetch, no gating, and no interactive control beyond the back button — the
 * content is a static, hard-coded catalogue (`useAboutSections`), so `onInit`
 * is a no-op.
 *
 * The bar is the shared `ExpauseAppBar`
 * (`presentation/components/common/appBar/ExpauseAppBar.tsx`), the web port of
 * Flutter's `BaseAppBar` (`lib/presentation/custom_widgets/base_app_bar.dart:5-42`)
 * that `AboutScreen` itself uses (`about_screen.dart:23-28`). The rest of the
 * structure still mirrors `SupportPage.tsx`: same `useBasePage` + `screenKey`
 * + `useKeepAlive` wiring, same centered-column body, same deep-link back
 * guard (a superset of Flutter's plain `NavigationService.pop()`,
 * `about_screen.dart:11-14`).
 *
 * Moving from the hand-rolled bar to `ExpauseAppBar` makes three intentional
 * visual changes: the title steps from `responsive.font.lg`/`semibold` to
 * `responsive.font.xl`/`bold`, the bar becomes sticky with a blurred
 * 50%-opacity backdrop, and the top safe-area inset is now honoured.
 *
 * **Web deviation from `presentation.md`'s "always use `VirtualList`" rule:**
 * the About content is a fixed four-section catalogue, so the sections are
 * mapped directly rather than through `VirtualList` — the task plan
 * (task-prompt line 1: "For rendering the list items we don't need to use the
 * VirtualList") explicitly authorises this, mirroring how `SupportPage.tsx`
 * documents the same list deviation.
 *
 * Accessible without authentication; mounted under `PUBLIC_ROUTES`.
 */
import { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { Navigator } from '@/presentation/routing/Navigator';
import { ExpauseAppBar } from '@/presentation/components/common/appBar/ExpauseAppBar';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { useAboutSections } from './hooks/useAboutSections';
import { AboutSectionCard } from './components/AboutSectionCard';

export const AboutPage = () => {
    const { theme } = useTheme();
    const responsive = useResponsiveDesign();
    const { l10n } = useLocale();
    const location = useLocation();

    // Canonical screen-key wiring (mirrors `SupportPage` / `ContactPage`).
    const { currentScreenKeyRef } = useKeepAlive();
    const screenKeyRef = useRef<string>(
        currentScreenKeyRef.current || generateScreenKey(location.pathname),
    );
    const screenKey = screenKeyRef.current;

    // Static content — no fetch, so `onInit` is a no-op.
    useBasePage({ screenKey, onInit: () => { } });

    const sections = useAboutSections();

    // Deep-link guard: with no in-app history, fall back to
    // the feed (same pattern as SupportPage).
    const handleBack = () => Navigator.goBackOr(() => Navigator.navigateToFeed());

    return (
        <div
            {...qaAttr({ id: 'about', status: 'ready' })}
            style={{
                minHeight: '100vh',
                backgroundColor: theme.background,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* App bar — back button + centered title. */}
            <ExpauseAppBar
                rootQa={{ id: 'about-app-bar' }}
                title={l10n.helpAbout.about}
                leadingQa={{ id: 'about-back' }}
                onLeadingTap={handleBack}
            />

            {/* Body — centered scrolling column of section cards. */}
            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: `${responsive.spacing.lg}px`,
                }}
            >
                <div
                    style={{
                        width: '100%',
                        maxWidth: responsive.dialog.maxWidth,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: responsive.spacing.lg,
                    }}
                >
                    {sections.map((section, index) => (
                        <AboutSectionCard
                            key={section.title}
                            section={section}
                            index={index}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};
